import React, { useState, useRef } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select as MuiSelect, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Typography,
  Chip,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Button,
  Slider,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { ExpandMore, Add, Delete } from "@mui/icons-material";
import { useTextPersistence } from "qwanyx";

export const TextArea = ({
  name,
  qwantumId,
  value,
  inputName,
  onSave,
  hidden,
  style,
  placeholder = "Enter text here...",
  // TextArea specific
  rows = 4,
  cols,
  readonly = false,
  disabled = false,
  allowEditing = true, // New flag for textarea editing
  resize = "vertical",
  wrap = "soft",
  // Bulma specific
  bulmaSize = "",
  bulmaColor = "",
  bulmaState = "",
  hasFixedSize = false,
  isLoading = false,
  // Label
  labelText = "",
  labelFontSize = 14,
  showSemicolon = true,
  labelBold = true,
  labelItalic = false,
  fontFamily = "inherit",
  fontSize = 12,
  // Dimensions
  width = "100%",
  height,
  maxWidth,
  minWidth,
  maxHeight,
  minHeight,
  flex,
  flexGrow,
  flexShrink,
  flexBasis,
  // Position
  position = 'relative',
  top,
  left,
  right,
  bottom,
  unit = 'px',
  lockedEdges = { top: false, left: false, right: false, bottom: false },
  // Styling
  backgroundColor,
  borderRadius,
  padding,
  cssClass,
  textareaClass,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp },
    parent
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    parent: state.data.parent
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const { text, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  const [prevValue, setPrevValue] = useState(value || text || "");
  const textareaRef = useRef(null);

  if (hidden) return null;

  const handleBlur = (e) => {
    const newValue = e.target.value;
    
    // Handle persistence only on blur
    if (newValue !== prevValue) {
      if (onSave) {
        onSave(newValue);
      }
      setPrevValue(newValue);
      
      // Update CraftJS props on blur
      if (enabled || allowEditing) {
        setProp(props => props.value = newValue);
      }
    }
    
    if (qwantumId) {
      persistenceBlur(e);
    }
  };

  // Selection styling
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const getPositionStyle = () => {
    if (position === 'absolute' || position === 'relative') {
      const positionStyle = { position };
      
      if (lockedEdges.top && top !== undefined) {
        positionStyle.top = `${top}${unit}`;
      }
      if (lockedEdges.left && left !== undefined) {
        positionStyle.left = `${left}${unit}`;
      }
      if (lockedEdges.right && right !== undefined) {
        positionStyle.right = `${right}${unit}`;
      }
      if (lockedEdges.bottom && bottom !== undefined) {
        positionStyle.bottom = `${bottom}${unit}`;
      }
      
      return positionStyle;
    }
    return { position };
  };

  const formatSize = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  // Build Bulma classes
  const buildTextareaClasses = () => {
    const classes = ['textarea'];
    if (bulmaSize) classes.push(bulmaSize);
    if (bulmaColor) classes.push(bulmaColor);
    if (bulmaState) classes.push(bulmaState);
    if (hasFixedSize) classes.push('has-fixed-size');
    return classes.join(' ');
  };

  const buildControlClasses = () => {
    const classes = ['control'];
    if (isLoading) {
      classes.push('is-loading');
      if (bulmaSize) classes.push(bulmaSize);
    }
    if (textareaClass) classes.push(textareaClass);
    return classes.join(' ');
  };

  const renderTextarea = () => (
    <textarea
      ref={textareaRef}
      name={inputName}
      rows={rows}
      cols={cols}
      defaultValue={value || text || ""}
      placeholder={placeholder}
      readOnly={readonly}
      disabled={disabled}
      wrap={wrap}
      onBlur={handleBlur}
      className={buildTextareaClasses()}
      style={{
        fontFamily: fontFamily,
        fontSize: `${fontSize}px`,
        width: "100%",
        height: formatSize(height),
        maxHeight: formatSize(maxHeight),
        minHeight: formatSize(minHeight),
        resize: resize
      }}
    />
  );

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      className={cssClass}
      style={{
        transition: 'all 0.2s ease',
        width: formatSize(width),
        maxWidth: formatSize(maxWidth),
        minWidth: formatSize(minWidth),
        flex: flex,
        flexGrow: flexGrow,
        flexShrink: flexShrink,
        flexBasis: flexBasis,
        boxSizing: 'border-box',
        backgroundColor: backgroundColor,
        borderRadius: borderRadius ? `${borderRadius}px` : undefined,
        padding: padding ? `${padding}px` : undefined,
        ...getPositionStyle(),
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {labelText && (
        <label
          style={{
            display: 'block',
            fontSize: `${labelFontSize}px`,
            fontFamily: fontFamily,
            fontWeight: labelBold ? 'bold' : 'normal',
            fontStyle: labelItalic ? 'italic' : 'normal',
            marginBottom: '4px',
            color: '#333'
          }}
        >
          {labelText}{showSemicolon ? ':' : ''}
        </label>
      )}
      
      <div className={buildControlClasses()}>
        {renderTextarea()}
      </div>
    </div>
  );
};

const TextAreaSettings = () => {
  const { 
    actions: { setProp }, 
    name, qwantumId, value, inputName, placeholder, rows, cols, readonly, disabled, allowEditing, resize, wrap,
    bulmaSize, bulmaColor, bulmaState, hasFixedSize, isLoading,
    labelText, labelFontSize, showSemicolon, labelBold, labelItalic, fontFamily, fontSize,
    width, height, maxWidth, minWidth, maxHeight, minHeight, flex, flexGrow, flexShrink, flexBasis,
    position, top, left, right, bottom, unit, lockedEdges,
    backgroundColor, borderRadius, padding, cssClass, textareaClass,
    parent
  } = useNode((node) => ({
    name: node.data.props.name,
    qwantumId: node.data.props.qwantumId,
    value: node.data.props.value,
    inputName: node.data.props.inputName,
    placeholder: node.data.props.placeholder,
    rows: node.data.props.rows,
    cols: node.data.props.cols,
    readonly: node.data.props.readonly,
    disabled: node.data.props.disabled,
    allowEditing: node.data.props.allowEditing,
    resize: node.data.props.resize,
    wrap: node.data.props.wrap,
    bulmaSize: node.data.props.bulmaSize,
    bulmaColor: node.data.props.bulmaColor,
    bulmaState: node.data.props.bulmaState,
    hasFixedSize: node.data.props.hasFixedSize,
    isLoading: node.data.props.isLoading,
    labelText: node.data.props.labelText,
    labelFontSize: node.data.props.labelFontSize,
    showSemicolon: node.data.props.showSemicolon,
    labelBold: node.data.props.labelBold,
    labelItalic: node.data.props.labelItalic,
    fontFamily: node.data.props.fontFamily,
    fontSize: node.data.props.fontSize,
    width: node.data.props.width,
    height: node.data.props.height,
    maxWidth: node.data.props.maxWidth,
    minWidth: node.data.props.minWidth,
    maxHeight: node.data.props.maxHeight,
    minHeight: node.data.props.minHeight,
    flex: node.data.props.flex,
    flexGrow: node.data.props.flexGrow,
    flexShrink: node.data.props.flexShrink,
    flexBasis: node.data.props.flexBasis,
    position: node.data.props.position,
    top: node.data.props.top,
    left: node.data.props.left,
    right: node.data.props.right,
    bottom: node.data.props.bottom,
    unit: node.data.props.unit,
    lockedEdges: node.data.props.lockedEdges,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    cssClass: node.data.props.cssClass,
    textareaClass: node.data.props.textareaClass,
    parent: node.data.props.parent
  }));

  const { query } = useEditor();
  const parentNode = parent ? query.node(parent).get() : null;

  // Bulma textarea modifiers
  const bulmaColors = [
    { value: '', label: 'Default' },
    { value: 'is-primary', label: 'Primary' },
    { value: 'is-link', label: 'Link' },
    { value: 'is-info', label: 'Info' },
    { value: 'is-success', label: 'Success' },
    { value: 'is-warning', label: 'Warning' },
    { value: 'is-danger', label: 'Danger' }
  ];

  const bulmaSizes = [
    { value: '', label: 'Normal' },
    { value: 'is-small', label: 'Small' },
    { value: 'is-medium', label: 'Medium' },
    { value: 'is-large', label: 'Large' }
  ];

  const bulmaStates = [
    { value: '', label: 'Normal' },
    { value: 'is-hovered', label: 'Hovered' },
    { value: 'is-focused', label: 'Focused' },
    { value: 'is-active', label: 'Active' }
  ];

  const resizeOptions = [
    { value: 'none', label: 'None' },
    { value: 'both', label: 'Both' },
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' }
  ];

  const wrapOptions = [
    { value: 'soft', label: 'Soft' },
    { value: 'hard', label: 'Hard' },
    { value: 'off', label: 'Off' }
  ];

  const fontFamilies = [
    { value: 'inherit', label: 'Inherit' },
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' },
    { value: "'Times New Roman', serif", label: 'Times New Roman' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: "'Courier New', monospace", label: 'Courier New' },
    { value: 'Verdana, sans-serif', label: 'Verdana' }
  ];

  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  return (
    <div>
      {parentNode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Parent: {parentNode.data.displayName || parentNode.data.name || 'Container'}</FormLabel>
        </FormControl>
      )}

      {/* Basic Properties */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Basic Properties</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Component Name</FormLabel>
            <TextField
              value={name || ''}
              onChange={(e) => setProp(props => props.name = e.target.value)}
              size="small"
              placeholder="Enter component name"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Input Name</FormLabel>
            <TextField
              value={inputName || ''}
              onChange={(e) => setProp(props => props.inputName = e.target.value)}
              size="small"
              placeholder="Enter input name attribute"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Quantum ID</FormLabel>
            <TextField
              value={qwantumId || ''}
              onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
              size="small"
              placeholder="Enter quantum ID for persistence"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Placeholder</FormLabel>
            <TextField
              value={placeholder || ''}
              onChange={(e) => setProp(props => props.placeholder = e.target.value)}
              size="small"
              placeholder="Enter placeholder text"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Default Value</FormLabel>
            <TextField
              value={value || ''}
              onChange={(e) => setProp(props => props.value = e.target.value)}
              size="small"
              multiline
              rows={2}
              placeholder="Enter default value"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Rows</FormLabel>
            <TextField
              value={rows || 4}
              onChange={(e) => setProp(props => props.rows = parseInt(e.target.value) || 4)}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 50 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Columns</FormLabel>
            <TextField
              value={cols || ''}
              onChange={(e) => setProp(props => props.cols = parseInt(e.target.value) || undefined)}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 200 }}
              placeholder="Leave empty for auto width"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Wrap</FormLabel>
            <MuiSelect
              value={wrap || 'soft'}
              onChange={(e) => setProp(props => props.wrap = e.target.value)}
              size="small"
            >
              {wrapOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Resize</FormLabel>
            <MuiSelect
              value={resize || 'vertical'}
              onChange={(e) => setProp(props => props.resize = e.target.value)}
              size="small"
            >
              {resizeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={readonly}
                onChange={(e) => setProp(props => props.readonly = e.target.checked)}
              />
            }
            label="Read only"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={disabled}
                onChange={(e) => setProp(props => props.disabled = e.target.checked)}
              />
            }
            label="Disabled"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allowEditing}
                onChange={(e) => setProp(props => props.allowEditing = e.target.checked)}
              />
            }
            label="Allow Editing"
          />

          <FormControl fullWidth margin="normal">
            <FormLabel>CSS Class (Container)</FormLabel>
            <TextField
              value={cssClass || ''}
              onChange={(e) => setProp(props => props.cssClass = e.target.value)}
              size="small"
              placeholder="Enter CSS class names for container"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>CSS Class (TextArea)</FormLabel>
            <TextField
              value={textareaClass || ''}
              onChange={(e) => setProp(props => props.textareaClass = e.target.value)}
              size="small"
              placeholder="Enter CSS class names for textarea control"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Bulma Styling */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Bulma Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Color</FormLabel>
            <MuiSelect
              value={bulmaColor || ''}
              onChange={(e) => setProp(props => props.bulmaColor = e.target.value)}
              size="small"
            >
              {bulmaColors.map(color => (
                <MenuItem key={color.value} value={color.value}>{color.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Size</FormLabel>
            <MuiSelect
              value={bulmaSize || ''}
              onChange={(e) => setProp(props => props.bulmaSize = e.target.value)}
              size="small"
            >
              {bulmaSizes.map(size => (
                <MenuItem key={size.value} value={size.value}>{size.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>State</FormLabel>
            <MuiSelect
              value={bulmaState || ''}
              onChange={(e) => setProp(props => props.bulmaState = e.target.value)}
              size="small"
            >
              {bulmaStates.map(state => (
                <MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={hasFixedSize}
                onChange={(e) => setProp(props => props.hasFixedSize = e.target.checked)}
              />
            }
            label="Fixed size (disable resize)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isLoading}
                onChange={(e) => setProp(props => props.isLoading = e.target.checked)}
              />
            }
            label="Loading state"
          />
        </AccordionDetails>
      </Accordion>

      {/* Label Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Label Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Label Text</FormLabel>
            <TextField
              value={labelText || ''}
              onChange={(e) => setProp(props => props.labelText = e.target.value)}
              size="small"
              placeholder="Enter label text"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Label Font Size</FormLabel>
            <TextField
              value={labelFontSize || 14}
              onChange={(e) => setProp(props => props.labelFontSize = parseInt(e.target.value) || 14)}
              size="small"
              type="number"
              inputProps={{ min: 8, max: 72 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Font Family</FormLabel>
            <MuiSelect
              value={fontFamily || 'inherit'}
              onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
              size="small"
            >
              {fontFamilies.map(font => (
                <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Text Font Size</FormLabel>
            <TextField
              value={fontSize || 12}
              onChange={(e) => setProp(props => props.fontSize = parseInt(e.target.value) || 12)}
              size="small"
              type="number"
              inputProps={{ min: 8, max: 72 }}
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={labelBold}
                onChange={(e) => setProp(props => props.labelBold = e.target.checked)}
              />
            }
            label="Bold label"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={labelItalic}
                onChange={(e) => setProp(props => props.labelItalic = e.target.checked)}
              />
            }
            label="Italic label"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showSemicolon}
                onChange={(e) => setProp(props => props.showSemicolon = e.target.checked)}
              />
            }
            label="Show semicolon after label"
          />
        </AccordionDetails>
      </Accordion>

      {/* Layout & Positioning */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Layout & Positioning</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Width</FormLabel>
            <TextField
              value={width || ''}
              onChange={(e) => setProp(props => props.width = e.target.value)}
              size="small"
              placeholder="100%, 300px, auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Height</FormLabel>
            <TextField
              value={height || ''}
              onChange={(e) => setProp(props => props.height = e.target.value)}
              size="small"
              placeholder="200px, 50vh, auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Max Width</FormLabel>
            <TextField
              value={maxWidth || ''}
              onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
              size="small"
              placeholder="400px, 50%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Min Width</FormLabel>
            <TextField
              value={minWidth || ''}
              onChange={(e) => setProp(props => props.minWidth = e.target.value)}
              size="small"
              placeholder="100px, 20%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Max Height</FormLabel>
            <TextField
              value={maxHeight || ''}
              onChange={(e) => setProp(props => props.maxHeight = e.target.value)}
              size="small"
              placeholder="400px, 50vh, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Min Height</FormLabel>
            <TextField
              value={minHeight || ''}
              onChange={(e) => setProp(props => props.minHeight = e.target.value)}
              size="small"
              placeholder="100px, 10vh, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Position</FormLabel>
            <MuiSelect
              value={position || 'relative'}
              onChange={(e) => setProp(props => props.position = e.target.value)}
              size="small"
            >
              <MenuItem value="relative">Relative</MenuItem>
              <MenuItem value="absolute">Absolute</MenuItem>
            </MuiSelect>
          </FormControl>

          {(position === 'absolute' || position === 'relative') && (
            <>
              <FormControl fullWidth margin="normal">
                <FormLabel>Unit</FormLabel>
                <ToggleButtonGroup
                  value={unit}
                  exclusive
                  onChange={(e, value) => value && setProp(props => props.unit = value)}
                  size="small"
                >
                  <ToggleButton value="px">px</ToggleButton>
                  <ToggleButton value="%">%</ToggleButton>
                </ToggleButtonGroup>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Position Controls</FormLabel>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.top || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                      />
                    }
                    label="Top"
                  />
                  <TextField
                    value={top || ''}
                    onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.top}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.left || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                      />
                    }
                    label="Left"
                  />
                  <TextField
                    value={left || ''}
                    onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.left}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.right || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                      />
                    }
                    label="Right"
                  />
                  <TextField
                    value={right || ''}
                    onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.right}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.bottom || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                      />
                    }
                    label="Bottom"
                  />
                  <TextField
                    value={bottom || ''}
                    onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.bottom}
                    style={{ width: '80px' }}
                  />
                </div>
              </FormControl>
            </>
          )}

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex</FormLabel>
            <TextField
              value={flex || ''}
              onChange={(e) => handleFlexChange('flex', e.target.value)}
              size="small"
              placeholder="1, 0 1 auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Grow</FormLabel>
            <TextField
              value={flexGrow || ''}
              onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
              size="small"
              placeholder="0, 1, 2, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Shrink</FormLabel>
            <TextField
              value={flexShrink || ''}
              onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
              size="small"
              placeholder="1, 0, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Basis</FormLabel>
            <TextField
              value={flexBasis || ''}
              onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
              size="small"
              placeholder="auto, 200px, 50%, or leave empty"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Container Styling */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Container Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal" component="fieldset">
            <FormLabel component="legend">Background Color</FormLabel>
            <input
              type="color"
              value={backgroundColor || '#ffffff'}
              onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Border Radius</FormLabel>
            <Slider
              value={borderRadius || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.borderRadius = value);
              }}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Padding</FormLabel>
            <Slider
              value={padding || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.padding = value);
              }}
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

TextArea.craft = {
  props: {
    name: "",
    qwantumId: "",
    value: "",
    inputName: "",
    placeholder: "Enter text here...",
    rows: 4,
    cols: null,
    readonly: false,
    disabled: false,
    allowEditing: true,
    resize: "vertical",
    wrap: "soft",
    bulmaSize: "",
    bulmaColor: "",
    bulmaState: "",
    hasFixedSize: false,
    isLoading: false,
    labelText: "",
    labelFontSize: 14,
    showSemicolon: true,
    labelBold: true,
    labelItalic: false,
    fontFamily: "inherit",
    fontSize: 12,
    width: "100%",
    height: null,
    maxWidth: null,
    minWidth: null,
    maxHeight: null,
    minHeight: null,
    flex: null,
    flexGrow: null,
    flexShrink: null,
    flexBasis: null,
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    unit: 'px',
    lockedEdges: { top: false, left: false, right: false, bottom: false },
    backgroundColor: '',
    borderRadius: 0,
    padding: 0,
    cssClass: '',
    textareaClass: ''
  },
  related: {
    settings: TextAreaSettings
  }
};